/* eslint-disable no-plusplus, no-continue, no-use-before-define, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useState, useEffect, useRef } from 'react';
import { X, ArrowRight, ArrowLeft, Download, Check, PackagePlus } from 'lucide-react';
import { FlavorDetails, FlavorRepository } from '../repositories/LocalDatabase';
import { showHUD } from './NotificationHUD';
import logo from '../assets/logo.png';



interface FlavorCardPopupProps {
  flavor: FlavorDetails;
  onClose: () => void;
  /** 所持状態が変更された際に呼ばれるコールバック */
  onOwnedChange?: () => void;
}

export default function FlavorCardPopup({
  flavor,
  onClose,
  onOwnedChange,
}: FlavorCardPopupProps) {
  const [isClosing, setIsClosing] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [isOwned, setIsOwned] = useState(flavor.owned);
  const [owningInProgress, setOwningInProgress] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Responsive scale
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      // 400px is the card width, 40px is for some margin
      if (width < 440) {
        setScale((width - 40) / 400);
      } else {
        setScale(1);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleClose = () => {
    const animationsEnabled = localStorage.getItem('animationsEnabled') !== 'false';
    if (!animationsEnabled) {
      onClose();
      return;
    }
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 500);
  };

  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const shareUrl = `https://hf.app/f/${flavor.id.substring(0, 8)}`;

    import('../utils/qrCodeUtils').then(({ generateQrPlain }) => {
      generateQrPlain(shareUrl).then((dataUrl) => {
        const qrImg = new Image();
        qrImg.onload = () => {
          canvas.width = qrImg.width;
          canvas.height = qrImg.height;
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(qrImg, 0, 0, canvas.width, canvas.height);
        };
        qrImg.src = dataUrl;
      }).catch(console.error);
    });
  }, [flavor.id]);

  const handleSaveQr = () => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;

    const url = canvas.toDataURL('image/png');
    const filename = `${flavor.name}_QR.png`;
    import('../utils/imageUtils').then(({ saveImageFile }) => {
      saveImageFile(url, filename).then((success) => {
        if (success) {
          setDownloadSuccess(true);
          setTimeout(() => setDownloadSuccess(false), 2000);
        }
      });
    });

    // Success handled in the promise
  };

  const frontCardRef = useRef<HTMLDivElement>(null);
  const backCardRef = useRef<HTMLDivElement>(null);

  const handleSaveCardImage = async () => {
    if (!frontCardRef.current || !backCardRef.current) return;
    try {
      const captureNode = document.createElement('div');
      captureNode.style.display = 'flex';
      captureNode.style.gap = '40px';
      captureNode.style.position = 'fixed';
      captureNode.style.left = '-9999px';
      captureNode.style.top = '0';
      captureNode.style.padding = '40px';
      captureNode.style.backgroundColor = '#1a1c1f';
      captureNode.style.borderRadius = '20px';

      // Clone Front
      const frontClone = frontCardRef.current.cloneNode(true) as HTMLElement;
      frontClone.style.transform = 'none';
      frontClone.style.position = 'relative'; // remove absolute if any
      frontClone.style.width = '400px';
      frontClone.style.height = '480px';
      frontClone.style.flexShrink = '0';
      captureNode.appendChild(frontClone);

      // Clone Back
      const backClone = backCardRef.current.cloneNode(true) as HTMLElement;
      backClone.style.transform = 'none'; // removes rotateY(180deg)
      backClone.style.position = 'relative';
      backClone.style.width = '400px';
      backClone.style.height = '480px';
      backClone.style.flexShrink = '0';
      captureNode.appendChild(backClone);

      document.body.appendChild(captureNode);

      // Copy canvases for Front
      const originalCanvasesFront = frontCardRef.current.querySelectorAll('canvas');
      const clonedCanvasesFront = frontClone.querySelectorAll('canvas');
      originalCanvasesFront.forEach((canvas, i) => {
        const ctx = clonedCanvasesFront[i].getContext('2d');
        if (ctx) ctx.drawImage(canvas, 0, 0);
      });

      // Copy canvases for Back (QR code)
      const originalCanvasesBack = backCardRef.current.querySelectorAll('canvas');
      const clonedCanvasesBack = backClone.querySelectorAll('canvas');
      originalCanvasesBack.forEach((canvas, i) => {
        const ctx = clonedCanvasesBack[i].getContext('2d');
        if (ctx) ctx.drawImage(canvas, 0, 0);
      });

      await new Promise((resolve) => setTimeout(resolve, 50));
      
      // Clean up ripple effects from cloned buttons
      captureNode.querySelectorAll('.ripple-effect').forEach((el) => el.remove());

      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(captureNode, {
        scale: 2,
        backgroundColor: '#1a1c1f',
        useCORS: true,
        logging: false,
      });

      document.body.removeChild(captureNode);

      const url = canvas.toDataURL('image/png');
      const filename = `hookah-forge-${flavor.name.replace(/\s+/g, '_')}_両面.png`;
      const { saveImageFile } = await import('../utils/imageUtils');
      await saveImageFile(url, filename);
    } catch (err) {
      console.error('Failed to generate card PNG', err);
    }
  };

  const handleToggleOwn = async () => {
    if (owningInProgress) return;
    setOwningInProgress(true);
    const nextOwned = !isOwned;
    try {
      await FlavorRepository.saveUserFlavor(
        flavor.id,
        nextOwned,
        undefined,
        undefined,
        undefined,
        flavor.nicotine,
      );
      setIsOwned(nextOwned);
      showHUD({
        type: 'success',
        message: nextOwned
          ? `${flavor.name} を所持済みにしました`
          : `${flavor.name} を未所持に戻しました`,
      });
      onOwnedChange?.();
    } catch (e) {
      showHUD({ type: 'error', message: '更新に失敗しました' });
    } finally {
      setOwningInProgress(false);
    }
  };

  const animationsEnabled = localStorage.getItem('animationsEnabled') !== 'false';

  return (
    <div
      className={`modal-backdrop ${isClosing ? 'closing' : ''}`}
      onClick={handleClose}
      style={{
        zIndex: 3000,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        animation: !animationsEnabled ? 'none' : (isClosing ? 'fadeOut 0.5s ease-in forwards' : 'fadeIn 0.5s ease-out'),
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(26, 28, 31, 0.85)',
          backdropFilter: 'blur(10px)',
          zIndex: -1,
          pointerEvents: 'none',
        }}
      />
      <div
        className="card-container"
        onClick={(e) => e.stopPropagation()}
        style={{
          '--card-scale': scale,
          animation: !animationsEnabled ? 'none' : (isClosing ? 'modalScaleDown 0.5s cubic-bezier(0.25, 1, 0.5, 1) forwards' : 'none'),
        } as React.CSSProperties}
      >
          <div className={`card-flipper ${isFlipped ? 'flipped' : ''}`}>
          {/* FRONT SIDE (表面) */}
          <div
            ref={frontCardRef}
            className="card-face front"
            style={{
              pointerEvents: isFlipped ? 'none' : 'auto',
            }}
          >
            {/* Close Button */}
            <button
              type="button"
              onClick={handleClose}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'rgba(231, 228, 223, 0.08)',
                borderRadius: '50%',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                color: 'var(--color-text)',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <X size={18} />
            </button>

            {/* Content Top */}
            <div style={{ marginTop: '20px', textAlign: 'center', width: '100%' }}>
              <span
                style={{
                  fontSize: '11px',
                  color: 'var(--color-accent)',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontWeight: 'bold',
                  display: 'block',
                }}
              >
                フレーバーカード
              </span>
              <h2
                style={{
                  fontSize: '28px',
                  fontFamily: 'var(--font-heading-ja)',
                  marginTop: '8px',
                  lineHeight: '1.4',
                  fontWeight: 'bold',
                }}
              >
                {flavor.name}
              </h2>
              <span
                style={{
                  fontSize: '13px',
                  color: 'rgba(231, 228, 223, 0.7)',
                  marginTop: '4px',
                  display: 'block',
                }}
              >
                {flavor.brand_name}
              </span>
            </div>

            {/* Content Middle: Description & Category */}
            <div
              style={{
                flexGrow: 1,
                flexShrink: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '12px',
                margin: '30px 0',
                minHeight: 0,
                textAlign: 'center',
                padding: '0 20px',
              }}
            >
              {flavor.series_name && (
                <span
                  style={{
                    fontSize: '13px',
                    color: 'var(--color-accent)',
                    backgroundColor: 'rgba(201, 166, 107, 0.1)',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    border: '1px solid rgba(201, 166, 107, 0.2)',
                  }}
                >
                  系統: {flavor.series_name}
                </span>
              )}
              {flavor.description && (
                <p
                  style={{
                    fontSize: '14px',
                    color: 'rgba(231, 228, 223, 0.6)',
                    lineHeight: '1.5',
                    margin: 0,
                    fontStyle: 'italic',
                    maxWidth: '320px',
                    display: '-webkit-box',
                    WebkitLineClamp: 4,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {flavor.description}
                </p>
              )}
            </div>

            {/* Toggle Own Button — 罫線直上・中央配置 */}
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginBottom: '14px' }}>
              <button
                type="button"
                onClick={handleToggleOwn}
                disabled={owningInProgress}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '7px',
                  padding: '8px 20px',
                  borderRadius: '20px',
                  backgroundColor: isOwned
                    ? 'rgba(91, 161, 139, 0.18)'
                    : 'rgba(231, 228, 223, 0.07)',
                  border: isOwned
                    ? '1px solid rgba(91, 161, 139, 0.45)'
                    : '1px solid rgba(231, 228, 223, 0.18)',
                  color: isOwned ? 'var(--color-success)' : 'rgba(231, 228, 223, 0.55)',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  cursor: owningInProgress ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  opacity: owningInProgress ? 0.6 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!owningInProgress) {
                    e.currentTarget.style.backgroundColor = isOwned
                      ? 'rgba(91, 161, 139, 0.28)'
                      : 'rgba(231, 228, 223, 0.13)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!owningInProgress) {
                    e.currentTarget.style.backgroundColor = isOwned
                      ? 'rgba(91, 161, 139, 0.18)'
                      : 'rgba(231, 228, 223, 0.07)';
                  }
                }}
              >
                {isOwned ? <Check size={15} /> : <PackagePlus size={15} />}
                {isOwned ? '未所持にする' : '所持済みにする'}
              </button>
            </div>

            {/* Content Bottom */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto 1fr',
                alignItems: 'center',
                borderTop: '1px solid rgba(231, 228, 223, 0.08)',
                paddingTop: '20px',
                width: '100%',
              }}
            >
              {/* Left container */}
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <img
                  src={logo}
                  alt="Hookah Forge Logo"
                  style={{ height: '40px', opacity: 0.8 }}
                />
              </div>

              {/* Center container */}
              <div />

              {/* Right container */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button
                  type="button"
                  onClick={handleSaveCardImage}
                  style={{
                    background: 'rgba(231, 228, 223, 0.08)',
                    border: '1px solid rgba(231, 228, 223, 0.15)',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-text)',
                    cursor: 'pointer',
                  }}
                  aria-label="画像を保存"
                >
                  <Download size={18} />
                </button>

                <button
                  type="button"
                  onClick={() => setIsFlipped(true)}
                  style={{
                    background: 'rgba(231, 228, 223, 0.08)',
                    border: '1px solid rgba(231, 228, 223, 0.15)',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-accent)',
                    cursor: 'pointer',
                  }}
                  aria-label="裏面へ"
                >
                  <ArrowRight size={20} />
                </button>
              </div>
            </div>
          </div>

          {/* BACK SIDE (裏面) */}
          <div
            ref={backCardRef}
            className="card-face back"
            style={{
              pointerEvents: isFlipped ? 'auto' : 'none',
            }}
          >
            {/* Close Button */}
            <button
              type="button"
              onClick={handleClose}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'rgba(231, 228, 223, 0.08)',
                borderRadius: '50%',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                color: 'var(--color-text)',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <X size={18} />
            </button>

            {/* Back Header */}
            <div
              style={{ marginTop: '20px', textAlign: 'center', width: '100%' }}
            >
              <span
                style={{
                  fontSize: 'var(--font-size-caption)',
                  color: 'var(--color-accent)',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontWeight: 'bold',
                }}
              >
                フレーバーカード
              </span>
            </div>

            {/* Back QR Canvas Container */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '20px 0',
                backgroundColor: '#ffffff',
                padding: '12px',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
              }}
            >
              <canvas
                ref={canvasRef}
                width={300}
                height={300}
                style={{
                  width: '200px',
                  height: '200px',
                  display: 'block',
                }}
              />
            </div>

            {/* Back Actions */}
            <div
              style={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: '1fr auto 1fr',
                alignItems: 'center',
                borderTop: '1px solid rgba(231, 228, 223, 0.08)',
                paddingTop: '20px',
              }}
            >
              {/* Left container */}
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <button
                  type="button"
                  onClick={() => setIsFlipped(false)}
                  style={{
                    background: 'rgba(231, 228, 223, 0.08)',
                    border: '1px solid rgba(231, 228, 223, 0.15)',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-accent)',
                    cursor: 'pointer',
                  }}
                >
                  <ArrowLeft size={20} />
                </button>
              </div>

              {/* Center container */}
              <div />

              {/* Right container */}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleSaveQr}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'var(--color-primary)',
                    color: '#ffffff',
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    boxShadow: '0 4px 12px rgba(107, 78, 255, 0.25)',
                    cursor: 'pointer',
                    border: 'none',
                  }}
                  aria-label="QRコードを保存"
                >
                  {downloadSuccess ? <Check size={20} /> : <Download size={20} />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
